import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, Visibility, VisibilityOff } from "@mui/icons-material";

const OneTimeUserFormDialog = ({
  open,
  setOpen,
  departments,
  handleAddEmployeeSubmit,
  oneTimeUsers,
  editIndex,
  setEditIndex,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  selectedDepartment,
  setSelectedDepartment,
  departmentId,
  setDepartmentId,
  adderror,
  setAddError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const requiredAsteriskStyle = {
    color: "red",
    marginLeft: "4px",
    fontSize: "1.2em",
    verticalAlign: "top",
  };

  useEffect(() => {
    if (open) {
      if (editIndex !== null && oneTimeUsers[editIndex]) {
        // Edit mode: Pre-fill form with user data
        const user = oneTimeUsers[editIndex];
        setFirstName(user.firstName || "");
        setLastName(user.lastName || "");
        setEmail(user.email || "");
        setPassword(user.password || "");
        setDepartmentId(user.departmentId?.toString() || "");
        setSelectedDepartment(user.departmentName || "");
        setAddError("");
      } else {
        // Add mode: Reset form fields
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setShowPassword(false);
        setDepartmentId("");
        setSelectedDepartment("");
        setAddError("");
      }
    }
  }, [open, editIndex, oneTimeUsers]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setShowPassword(false);
        setDepartmentId("");
        setSelectedDepartment("");
        setAddError("");
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{ 
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
          maxWidth: { xs: '100%', sm: '500px' },
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E7E8E9",
          padding: { xs: "8px 16px", sm: "10px 24px" },
        }}
      >
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {editIndex !== null ? "Edit One Time User" : "Add One Time User"}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={() => {
            setOpen(false);
            setFirstName("");
            setLastName("");
            setEmail("");
            setPassword("");
            setShowPassword(false);
            setDepartmentId("");
            setSelectedDepartment("");
            setAddError("");
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: { xs: "16px", sm: "24px" } }}>
        <form onSubmit={handleAddEmployeeSubmit} id="add-one-time-user-form">
          {adderror && (
            <Box sx={{ 
              backgroundColor: "#ffebee", 
              color: "#c62828", 
              p: 1.5, 
              textAlign: "center", 
              mb: 2, 
              borderRadius: "8px",
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              {adderror}
            </Box>
          )}
          <div className="row">
            <div className="col-12 col-md-6 mb-3 mt-3">
              <label style={{ 
                fontWeight: 500, 
                marginBottom: 4, 
                display: "block", 
                textAlign: "left",
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                First Name<span style={requiredAsteriskStyle}>*</span>
              </label>
              <input
                required
                placeholder="Enter First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="form-control"
                style={{
                  height: { xs: '40px', sm: '48px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </div>
            <div className="col-12 col-md-6 mb-3 mt-3">
              <label style={{ 
                fontWeight: 500, 
                marginBottom: 4, 
                display: "block", 
                textAlign: "left",
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Last Name<span style={requiredAsteriskStyle}>*</span>
              </label>
              <input
                required
                placeholder="Enter Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="form-control"
                style={{
                  height: { xs: '40px', sm: '48px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </div>
            <div className="col-12 col-md-6 mb-3">
              <label style={{ 
                fontWeight: 500, 
                marginBottom: 4, 
                display: "block", 
                textAlign: "left",
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Email<span style={requiredAsteriskStyle}>*</span>
              </label>
              <input
                required
                placeholder="Enter Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                style={{
                  height: { xs: '40px', sm: '48px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </div>
            <div className="col-12 col-md-6 mb-3" style={{ position: "relative" }}>
              <label style={{ 
                fontWeight: 500, 
                marginBottom: 4, 
                display: "block", 
                textAlign: "left",
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Password<span style={requiredAsteriskStyle}>*</span>
              </label>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                required
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  height: { xs: '40px', sm: '48px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
              <span
                className="position-absolute"
                style={{ 
                  top: isMobile ? "36px" : "44px", 
                  right: "18px", 
                  cursor: "pointer" 
                }}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </span>
            </div>
          </div>
          <Box sx={{ mb: 2 }}>
            <label style={{ 
              fontWeight: 500, 
              marginBottom: 4, 
              display: "block", 
              textAlign: "left",
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              Department
            </label>
            <select
              value={departmentId}
              onChange={(e) => {
                const deptId = Number(e.target.value);
                const dept = departments.find((d) => d.id === deptId);
                setDepartmentId(deptId || "");
                setSelectedDepartment(dept?.name || "");
              }}
              className="form-control"
              style={{
                height: { xs: '40px', sm: '48px' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              <option value="" disabled>
                Select Department
              </option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </Box>
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          padding: { xs: "0.5rem", sm: "1rem" },
          borderTop: "1px solid #E7E8E9",
          justifyContent: "center",
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1
        }}
      >
        <button 
          type="submit" 
          form="add-one-time-user-form" 
          className="btn BlueBtn"
          style={{
            padding: { xs: "8px 16px", sm: "8px 24px" },
            fontSize: { xs: "0.875rem", sm: "1rem" },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          {editIndex !== null ? "Update" : "Create"}
        </button>
        <button
          type="button"
          className="btn btn-outlineWhite"
          onClick={() => {
            setFirstName("");
            setLastName("");
            setEmail("");
            setPassword("");
            setShowPassword(false);
            setDepartmentId("");
            setSelectedDepartment("");
            setAddError("");
          }}
          style={{
            padding: { xs: "8px 16px", sm: "8px 24px" },
            fontSize: { xs: "0.875rem", sm: "1rem" },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Clear
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default OneTimeUserFormDialog;